/**
 * Shared helpers for the admin panels (used by the admin portal and the web app's admin view).
 *
 * - authHeaders(): the session token, under the one key both apps use (eurion_token).
 * - jsonInit(): Content-Type only when there IS a body. Sending it on an empty DELETE/POST
 *   makes Fastify reject the request (400 FST_ERR_CTP_EMPTY_JSON_BODY): that silently broke
 *   every delete/verify/accept/suspend/sync button.
 * - showAdminError(): a small self-mounting toast, so a failed action is never silent.
 */
import { gt } from './i18nUtil';

export function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('eurion_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function jsonInit(method: string, body?: unknown, extra?: Record<string, string>): RequestInit {
  const headers: Record<string, string> = { ...authHeaders(), ...extra };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  return { method, headers, body: body !== undefined ? JSON.stringify(body) : undefined };
}

/** Error text from a failed response (Eurion error envelope, else the HTTP status). */
export async function errorMessage(res: Response): Promise<string> {
  try {
    const body = await res.json() as { error?: { message?: string } | string; message?: string };
    const e = body.error;
    return (typeof e === 'string' ? e : e?.message) ?? body.message ?? `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

let toastHost: HTMLDivElement | null = null;

export function showAdminError(message: string): void {
  if (typeof document === 'undefined') return;
  if (!toastHost) {
    toastHost = document.createElement('div');
    toastHost.setAttribute('role', 'alert');
    toastHost.style.cssText = 'position:fixed;right:16px;bottom:16px;z-index:9999;display:flex;flex-direction:column;gap:8px;max-width:420px';
    document.body.appendChild(toastHost);
  }
  const el = document.createElement('div');
  el.textContent = message;
  el.style.cssText = 'background:#BF616A;color:#ECEFF4;padding:10px 14px;border-radius:10px;font-size:13px;box-shadow:0 4px 16px rgba(0,0,0,.35);cursor:pointer';
  el.title = gt('adminUi.clickToDismiss', 'Click to dismiss');
  el.onclick = () => el.remove();
  toastHost.appendChild(el);
  setTimeout(() => el.remove(), 8000);
}
